import React, { FC, useCallback, useState } from "react";
import NextImage, { ImageProps } from "next/image";
import { classNames } from "../../utils/class-names";

export const placeholderUnknownLogoURL = "/images/logos/unknown.png";

const BAD_SRCS: { [src: string]: true } = {};

type LogoProps = {
  srcs: string[];
  size: number | LogoSize;
  alt?: string;
  className?: string;
};

export type LogoSize = "sm" | "md" | "lg" | "xl";

export const logoSizeMap: Record<LogoSize, number> = {
  sm: 16,
  md: 24,
  lg: 30,
  xl: 36,
};

/**
 * Renders an image by sequentially trying a list of URIs, and then eventually a fallback triangle alert
 */
export const Logo: FC<LogoProps> = ({
  srcs,
  size,
  alt = "",
  className,
  ...rest
}) => {
  const [, refresh] = useState<number>(0);
  const src = srcs.find((src) => !BAD_SRCS[src]) || placeholderUnknownLogoURL;

  const normalizedSize = typeof size === "string" ? logoSizeMap[size] : size;
  const [aspectRatio, setAspectRatio] = useState(1);
  const height = normalizedSize;
  const width = Math.round(aspectRatio * normalizedSize);

  const onLoadingComplete = useCallback(
    ({ naturalHeight, naturalWidth }: HTMLImageElement) => {
      if (naturalHeight === 0 || naturalWidth === 0) {
        return;
      }

      setAspectRatio(naturalWidth / naturalHeight);
    },
    [setAspectRatio]
  );

  return (
    <div className="rounded" style={{ width, height }}>
      <div style={{ width, height }} className="overflow-hidden rounded">
        <NextImage
          src={src}
          width={width}
          onError={() => {
            if (src) BAD_SRCS[src] = true;
            refresh((i) => i + 1);
          }}
          height={height}
          placeholder="empty"
          alt={alt}
          unoptimized={true}
          className={classNames("rounded", className)}
          onLoadingComplete={onLoadingComplete}
        />
      </div>
    </div>
  );
};
